/**
 * TopBar Component — Plan E E.5
 *
 * Application header with project info, build/deploy buttons, view-mode toggle,
 * and current tenant display.
 *
 * Plan E changes (atomic with E.6):
 *   - REMOVED hardcoded tenant dropdown `['acme-corp', 'demo-tenant', 'gamemagick']`
 *     (Rule #6 violation inherited from pre-Plan-D legacy admin).
 *   - Tenant label reads from `tenantStore.tenantId` (sourced via GET /api/me).
 *   - Added Designer / Terminals view-mode toggle wired to `uiStore.viewMode`.
 */
import { useEffect, useState } from 'react';
import { Play, Upload, Settings, Zap, Cloud, Code, Terminal as TerminalIcon, Loader2, X, ChevronDown, Check, Plus, Menu, BookOpen, Boxes, Globe, LayoutDashboard, Trash2, Edit3, Copy, Snowflake } from 'lucide-react';
import { getBrand } from '@/config/brand';
import { useProjectStore } from '@/stores/projectStore';
import { useBuildStore } from '@/stores/buildStore';
import { useDeployStore } from '@/stores/deployStore';
import { useTenantStore } from '@/stores/tenantStore';
import { useUIStore } from '@/stores/uiStore';
import { useMindChatUiStore } from '@/stores/mindChatUiStore';
import { SettingsMenu } from '@/components/SettingsMenu';
import { MobileMenuDrawer } from './MobileMenuDrawer';
import { UserMenu } from './UserMenu';
import { HelpButton } from '@/components/help/HelpButton';
import type { BuildStage } from '@/types';
import { tip } from '@/config/helpContent';
import clsx from 'clsx';

/**
 * E.21.31.2: map raw backend stage enum to user-facing label.
 * Falls back to "Building..." when the stage hasn't yet streamed.
 */
function stageLabel(stage: BuildStage | null): string {
  switch (stage) {
    case 'pending':
      return 'Starting...';
    case 'installing':
      return 'Installing dependencies...';
    case 'building':
      return 'Building...';
    case 'deploying':
      return 'Packaging...';
    default:
      return 'Building...';
  }
}

export function TopBar() {
  const { currentProject } = useProjectStore();
  // E.21.35: switcher dropdown reads the full projects[] list
  const projects = useProjectStore((s) => s.projects);
  const switchProject = useProjectStore((s) => s.switchProject);
  const createProject = useProjectStore((s) => s.createProject);
  const renameProject = useProjectStore((s) => s.renameProject);
  // Project-lifecycle (2026-07-28): the TopBar Delete is the TRUE permanent
  // purge (DELETE ?purge=true — hard-deletes the Forgejo repo + FS), matching
  // the IceBox "Delete permanently". Freeze (Snowflake, above) is the safe,
  // reversible path. The soft deleteProject is no longer wired to any UI.
  const purgeProject = useProjectStore((s) => s.purgeProject);
  const duplicateProject = useProjectStore((s) => s.duplicateProject);
  const archiveProject = useProjectStore((s) => s.archiveProject);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  // 2026-06-27 project-mgmt overhaul: name-on-create + inline rename + delete.
  const [creatingNew, setCreatingNew] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [renamingProjectId, setRenamingProjectId] = useState<string | null>(null);
  const [renamingProjectName, setRenamingProjectName] = useState('');
  // Phase B.1a (E.21.39): the Settings cog now opens a SettingsMenu
  // dropdown with the auto-rebuild-on-save toggle (and future SPA
  // config fields). State lives here so click-outside / escape can
  // be handled at this level if added later.
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  // 1.555.0: the MIND Chat 🧠 trigger lives here now (was a fixed FAB that
  // overlapped the Pi composer). Opens the panel rendered by MindChatWidget.
  const openMindChat = useMindChatUiStore((s) => s.open);
  const mindChatOpen = useMindChatUiStore((s) => s.isOpen);

  // E.21.36 Workstream (b): Deploy lifecycle moved off buildStore (which
  // had a dead stub deploymentStatus field) onto a dedicated useDeployStore.
  // Reviewer C-4: removed `deploymentStatus` from useBuildStore destructure;
  // useDeployStore is the authoritative source.
  const buildStatus = useBuildStore((s) => s.buildStatus);
  const deploymentStatus = useDeployStore((s) => s.deploymentStatus);
  const deployProject = useDeployStore((s) => s.deployProject);
  const pollDeployStatus = useDeployStore((s) => s.pollDeployStatus);
  const undeployProject = useDeployStore((s) => s.undeployProject);
  // 2026-06-27 — agent deploy confirm-gate. An agent (Pi/voice) can only STAGE
  // a public deploy (pendingDeploy); this banner is the only path that calls
  // confirmPendingDeploy → the real public publish.
  const pendingDeploy = useDeployStore((s) => s.pendingDeploy);
  const confirmPendingDeploy = useDeployStore((s) => s.confirmPendingDeploy);
  const cancelPendingDeploy = useDeployStore((s) => s.cancelPendingDeploy);
  // 2026-07-29 — the n8n / GHL / Payload / knowledge write confirm prompts moved
  // OUT of this topnav banner and INTO the Pi chat frame (PiChatConfirm), inline
  // like Claude's Allow. Their stores are unchanged; only the render location
  // moved. Deploy stays here (public-internet publish + voice-shared).
  // Two-click confirm for the destructive Undeploy (takes a public site down).
  const [confirmUndeploy, setConfirmUndeploy] = useState(false);
  const tenantId = useTenantStore((s) => s.tenantId);
  const viewMode = useUIStore((s) => s.viewMode);
  const setViewMode = useUIStore((s) => s.setViewMode);
  // 1.126.0 Phase 2c: hamburger toggles the MobileMenuDrawer. Subscribe
  // to mobileMenuOpen so the conditional mount below re-renders when the
  // hamburger click flips the store.
  const setMobileMenuOpen = useUIStore((s) => s.setMobileMenuOpen);
  const mobileMenuOpen = useUIStore((s) => s.mobileMenuOpen);

  // E.21.30 — fire-and-forget: start the build via POST, then begin polling
  // /status @ 1Hz. pollBuildStatus runs until terminal status (success/error)
  // or 10-min timeout, updating buildStore on completion. The InfoPanel
  // Problems tab subscribes to buildStatus.errors + .warnings.
  const startBuild = useBuildStore((s) => s.startBuild);
  const pollBuildStatus = useBuildStore((s) => s.pollBuildStatus);
  const cancelBuild = useBuildStore((s) => s.cancelBuild);

  const handleBuild = async () => {
    if (!currentProject) return;
    await startBuild(currentProject.id);
    void pollBuildStatus(currentProject.id);
  };

  // Gap A1 (2026-08-18): production build had NO UI control — the rail was
  // API-only ({"mode":"production"} → build:public) while the Build button
  // hardcoded preview. The chevron menu exposes both modes; the plain Build
  // click stays a preview build.
  const [buildMenuOpen, setBuildMenuOpen] = useState(false);
  // Escape closes the build menu — the FilesDrawer Phase 2f idiom: the
  // listener attaches only while open, so a closed menu costs nothing.
  useEffect(() => {
    if (!buildMenuOpen) return;
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setBuildMenuOpen(false);
    };
    document.addEventListener('keydown', onKeydown);
    return () => document.removeEventListener('keydown', onKeydown);
  }, [buildMenuOpen]);
  const handleBuildMode = async (mode?: 'production') => {
    if (!currentProject) return;
    setBuildMenuOpen(false);
    if (mode === 'production') {
      await startBuild(currentProject.id, 'production');
    } else {
      await startBuild(currentProject.id);
    }
    void pollBuildStatus(currentProject.id);
  };

  const handleCancel = () => {
    if (!currentProject) return;
    void cancelBuild(currentProject.id);
  };

  // E.21.36 Workstream (b): fire-and-forget deploy. POST /api/deploys/{id}
  // returns 200 with status='syncing'; pollDeployStatus tracks progress
  // until http-live (Phase A terminal) or https-live (Phase B daily cron).
  const handleDeploy = async () => {
    if (!currentProject) return;
    await deployProject(currentProject.id);
    void pollDeployStatus(currentProject.id);
  };

  // Reviewer Q4: disable Deploy button when:
  //   - no project loaded
  //   - mid-deploy (syncing | applying-nginx) — re-firing during nginx
  //     write window would corrupt the active rewrite
  const isDeployingNow =
    deploymentStatus.status === 'syncing' ||
    deploymentStatus.status === 'applying-nginx';

  // 2026-06-26 — Undeploy: only meaningful when the current project has a
  // LIVE public deploy. DELETE /api/deploys/{id} removes the VPS vhost+files.
  const isLiveDeploy =
    deploymentStatus.status === 'http-live' ||
    deploymentStatus.status === 'https-live';
  const isRemoving = deploymentStatus.status === 'removing';

  const handleUndeploy = async () => {
    if (!currentProject) return;
    setConfirmUndeploy(false);
    await undeployProject(currentProject.id);
  };

  return (
    <>
    {/* 2026-06-27 — agent deploy confirm-gate banner. Shown when an agent has
        STAGED a public deploy (deployStore.pendingDeploy). Deploy publishes to
        the PUBLIC internet with no auth, so a human must explicitly Confirm —
        the agent's deploy_project tool cannot publish on its own. */}
    {pendingDeploy && (
      <div
        data-testid="deploy-confirm-banner"
        data-phase={pendingDeploy.phase}
        className={
          'flex items-center justify-between gap-3 px-4 py-2 border-b text-vibe-text text-sm ' +
          (pendingDeploy.phase === 'done'
            ? 'bg-emerald-500/15 border-emerald-500/40'
            : pendingDeploy.phase === 'failed'
              ? 'bg-red-500/15 border-red-500/40'
              : 'bg-amber-500/15 border-amber-500/40')
        }
      >
        {/* ⭐⭐ THE BANNER OUTLIVES THE CLICK. It used to be unmounted by the
            double-fire guard the instant Confirm was pressed, so the publish
            ran and finished into silence — the operator's "as a user i dont
            know what i should do". The guard is a phase now, and these are the
            frames after `staged`. */}
        {pendingDeploy.phase === 'staged' && (
          <>
            <span>
              <strong>Publish publicly?</strong> An agent staged a deploy
              {pendingDeploy.slug ? <> as <code>{pendingDeploy.slug}</code></> : null}. This makes
              the project reachable on the public internet with no authentication.
            </span>
            <span className="flex items-center gap-2 flex-shrink-0">
              <button
                data-testid="deploy-confirm-accept"
                title={tip('deployConfirmAccept')}
                onClick={() => void confirmPendingDeploy()}
                className="px-3 py-1 rounded bg-amber-500 text-black font-medium hover:bg-amber-400 transition-colors"
              >
                Confirm &amp; publish
              </button>
              <button
                data-testid="deploy-confirm-cancel"
                onClick={() => cancelPendingDeploy()}
                className="px-3 py-1 rounded bg-vibe-bg-tertiary text-vibe-text hover:bg-vibe-border transition-colors"
              >
                Cancel
              </button>
            </span>
          </>
        )}

        {pendingDeploy.phase === 'publishing' && (
          /* ⚠ No control at all while in flight. A disabled button is still a
             control the eye goes to; the honest state here is a statement. */
          <span data-testid="deploy-confirm-publishing">
            <strong>Publishing…</strong> Syncing the site and applying the web
            server configuration. This usually takes a few seconds.
          </span>
        )}

        {pendingDeploy.phase === 'done' && (
          <>
            <span data-testid="deploy-confirm-done">
              {/* ⛔ THE LIVE URL IS THE ANSWER, so it is a real link and not
                  prose about one. A confirmation that produces no address is
                  indistinguishable from one that did nothing. */}
              <strong>Published.</strong>{' '}
              {deploymentStatus.url ? (
                <a
                  data-testid="deploy-confirm-url"
                  href={deploymentStatus.url}
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2"
                >
                  {deploymentStatus.url}
                </a>
              ) : (
                'The deploy reported success but returned no URL.'
              )}
              {deploymentStatus.deployedAt ? (
                <> · {new Date(deploymentStatus.deployedAt).toLocaleTimeString()}</>
              ) : null}
            </span>
            <button
              data-testid="deploy-confirm-dismiss"
              onClick={() => cancelPendingDeploy()}
              className="px-3 py-1 rounded bg-vibe-bg-tertiary text-vibe-text hover:bg-vibe-border transition-colors flex-shrink-0"
            >
              Dismiss
            </button>
          </>
        )}

        {pendingDeploy.phase === 'failed' && (
          <>
            <span data-testid="deploy-confirm-failed">
              {/* ⚠ The backend's own message, not a generic one — it is the
                  only diagnostic there is. */}
              <strong>Publish failed.</strong>{' '}
              {deploymentStatus.lastError ?? 'No reason was reported.'}
            </span>
            <button
              data-testid="deploy-confirm-dismiss"
              onClick={() => cancelPendingDeploy()}
              className="px-3 py-1 rounded bg-vibe-bg-tertiary text-vibe-text hover:bg-vibe-border transition-colors flex-shrink-0"
            >
              Dismiss
            </button>
          </>
        )}
      </div>
    )}
    {/* n8n / GHL / Payload / knowledge write-confirm prompts render inline in the
        Pi chat frame now (PiChatConfirm), not as topnav banners. Deploy above
        stays here by decision (public-internet publish + voice-shared). */}
    <div className="flex items-center justify-between h-12 px-4 bg-vibe-bg-secondary border-b border-vibe-border">
      {/* Left side - Logo and Project */}
      <div className="flex items-center gap-4">
        {/* 1.126.0 Phase 2c — hamburger button. Hidden on desktop
            (default `hidden`); shown on touch (`[@media(pointer:coarse)]:flex`).
            Opens MobileMenuDrawer for overflow controls. */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Open menu"
          className="hidden [@media(pointer:coarse)]:flex items-center justify-center p-2 text-vibe-text-secondary hover:text-vibe-text hover:bg-vibe-bg-tertiary rounded transition-colors [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
        >
          <Menu className="w-5 h-5" />
        </button>
        {/* Logo — 1.126.0 Phase 2c: hidden on touch (master plan +
            worldview both omit Logo from mobile header). */}
        <div className="flex items-center gap-2 [@media(pointer:coarse)]:hidden">
          {getBrand().logoUrl ? (
            <img
              src={getBrand().logoUrl}
              alt={`${getBrand().productNameVibe} logo`}
              className="w-5 h-5 rounded-full"
            />
          ) : (
            <Zap className="w-5 h-5 text-vibe-accent" />
          )}
          <span className="font-semibold text-vibe-text">{getBrand().productNameVibe}</span>
        </div>

        {/* Separator */}
        <div className="w-px h-6 bg-vibe-border" />

        {/* Project switcher dropdown — E.21.35. The trigger is the project
            name + chevron. Click toggles a dropdown listing all projects;
            click another project → switchProject(id). Click outside closes.
            data-testid="project-switcher" stable for Playwright. */}
        {currentProject && (
          <div className="flex items-center gap-2 relative">
            <button
              data-testid="project-switcher"
              title={tip('projectSwitcher')}
              onClick={() => setProjectMenuOpen((v) => !v)}
              className="flex items-center gap-1 min-w-0 text-sm text-vibe-text-secondary hover:text-vibe-text px-2 py-1 rounded hover:bg-vibe-bg-tertiary transition-colors [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
            >
              {/* 2026-07-02 — long names ("Model A/B Coding Tests") wrapped to
                  3 lines at narrow widths, bleeding above the TopBar into the
                  view-mode pills. Truncate with ellipsis; full name on hover. */}
              <span
                className="truncate whitespace-nowrap max-w-[220px]"
                title={currentProject.name}
              >
                {currentProject.name}
              </span>
              <ChevronDown className={clsx('w-3 h-3 shrink-0 transition-transform', projectMenuOpen && 'rotate-180')} />
            </button>
            {buildStatus.status === 'building' && (
              <span
                data-testid="build-status-pill"
                className="flex items-center gap-1.5 px-2 py-0.5 text-xs bg-vibe-warning/20 text-vibe-warning rounded"
              >
                <Loader2 className="w-3 h-3 animate-spin" />
                {/* E.21.31.2: surface verbatim backend stage so user sees
                    "Installing dependencies..." instead of a generic
                    "Building..." pill that masks the actual progress. */}
                {stageLabel(buildStatus.stage)}
              </span>
            )}
            {projectMenuOpen && (
              <>
                {/* Click-outside backdrop. Top-bar chrome menus sit at the
                    documented overlay band (backdrop z-[10006], menu z-[10007])
                    so they stack ABOVE the embedded briefing-room module's
                    fixed overlays (which reach z-1000) — not the legacy z-40/50
                    that the module content painted over. See FilesDrawer.tsx. */}
                <div
                  className="fixed inset-0 z-[10006]"
                  onClick={() => setProjectMenuOpen(false)}
                />
                <div
                  data-testid="project-switcher-menu"
                  className="absolute top-full left-0 mt-1 z-[10007] min-w-[16rem] max-h-96 overflow-y-auto bg-vibe-bg-secondary border border-vibe-border rounded shadow-lg"
                >
                  {projects.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-vibe-text-secondary italic">
                      No projects yet
                    </div>
                  ) : (
                    projects.map((p) => {
                      const isCurrent = p.id === currentProject.id;
                      const isRenaming = renamingProjectId === p.id;
                      return (
                        <div
                          key={p.id}
                          className={clsx(
                            'group w-full flex items-center gap-1 px-3 py-2 text-sm transition-colors',
                            isCurrent
                              ? 'bg-vibe-accent/10 text-vibe-text'
                              : 'text-vibe-text-secondary hover:bg-vibe-bg-tertiary hover:text-vibe-text',
                          )}
                        >
                          <Check className={clsx('w-3 h-3 flex-shrink-0', isCurrent ? 'text-vibe-accent' : 'invisible')} />
                          {isRenaming ? (
                            <input
                              autoFocus
                              data-testid="project-rename-input"
                              type="text"
                              value={renamingProjectName}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setRenamingProjectName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const trimmed = renamingProjectName.trim();
                                  if (trimmed && trimmed !== p.name) void renameProject(p.id, trimmed);
                                  setRenamingProjectId(null);
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  setRenamingProjectId(null);
                                }
                              }}
                              onBlur={() => setRenamingProjectId(null)}
                              className="flex-1 min-w-0 bg-vibe-bg text-vibe-text text-sm rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-vibe-accent"
                            />
                          ) : (
                            <button
                              data-testid={`project-switcher-item-${p.id}`}
                              onClick={() => {
                                setProjectMenuOpen(false);
                                void switchProject(p.id);
                              }}
                              className="flex-1 min-w-0 flex items-center gap-2 text-left"
                            >
                              <span className="flex-1 truncate">{p.name}</span>
                              <span
                                data-testid={`project-lastviewed-${p.id}`}
                                title="Last viewed"
                                className="text-xs text-vibe-text-secondary flex-shrink-0"
                              >
                                {/* Project-lifecycle (2026-07-28): "Last viewed"
                                    stamp, falling back to updated_at for projects
                                    created before the field existed. */}
                                Viewed {new Date(p.last_viewed_at ?? p.updated_at).toLocaleDateString()}
                              </span>
                            </button>
                          )}
                          {!isRenaming && (
                            <>
                              <button
                                data-testid={`project-rename-${p.id}`}
                                aria-label={`Rename ${p.name}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRenamingProjectId(p.id);
                                  setRenamingProjectName(p.name);
                                }}
                                className="opacity-0 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 p-1 rounded hover:bg-vibe-border text-vibe-text-secondary hover:text-vibe-text"
                              >
                                <Edit3 className="w-3 h-3" />
                              </button>
                              <button
                                data-testid={`project-duplicate-${p.id}`}
                                aria-label={`Duplicate ${p.name}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setProjectMenuOpen(false);
                                  void duplicateProject(p.id);
                                }}
                                className="opacity-0 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 p-1 rounded hover:bg-vibe-border text-vibe-text-secondary hover:text-vibe-text"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                              <button
                                data-testid={`project-archive-${p.id}`}
                                aria-label={`IceBox ${p.name}`}
                                title="→ IceBox This Project"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Freeze is reversible (project stays on disk) — no confirm.
                                  void archiveProject(p.id);
                                }}
                                className="opacity-0 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 p-1 rounded hover:bg-vibe-border text-vibe-text-secondary hover:text-vibe-text"
                              >
                                <Snowflake className="w-3 h-3" />
                              </button>
                              <button
                                data-testid={`project-delete-${p.id}`}
                                aria-label={`Delete ${p.name} permanently`}
                                title="Delete permanently"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (
                                    window.confirm(
                                      `Permanently delete “${p.name}”?\n\n` +
                                        `This erases its files AND its full Git history — it cannot be recovered.\n\n` +
                                        `To keep it recoverable, use Freeze (❄) instead.`,
                                    )
                                  ) {
                                    void purgeProject(p.id);
                                  }
                                }}
                                className="opacity-0 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 p-1 rounded hover:bg-vibe-border text-vibe-text-secondary hover:text-vibe-error"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })
                  )}
                  {/* + New Project — now NAME-ON-CREATE (was hardcoded
                      "Project N"). Clicking reveals an inline input. */}
                  {creatingNew ? (
                    <div className="border-t border-vibe-border px-3 py-2">
                      <input
                        autoFocus
                        data-testid="project-new-input"
                        type="text"
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const trimmed = newProjectName.trim();
                            if (!trimmed) return;
                            setProjectMenuOpen(false);
                            setCreatingNew(false);
                            setNewProjectName('');
                            void createProject(trimmed);
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            setCreatingNew(false);
                            setNewProjectName('');
                          }
                        }}
                        onBlur={() => { if (newProjectName === '') setCreatingNew(false); }}
                        placeholder="project name"
                        className="w-full bg-vibe-bg text-vibe-text text-sm rounded px-2 py-1 outline-none focus:ring-1 focus:ring-vibe-accent"
                      />
                    </div>
                  ) : (
                    <button
                      data-testid="project-switcher-new"
                      onClick={() => {
                        setCreatingNew(true);
                        setNewProjectName('');
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm border-t border-vibe-border text-vibe-accent hover:bg-vibe-bg-tertiary"
                    >
                      <Plus className="w-3 h-3" />
                      New Project
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Center - View mode toggle. 1.126.0 Phase 2c: hidden on touch
          (moved to MobileMenuDrawer). */}
      <div
        role="group"
        data-testid="view-mode-group"
        aria-label="View mode"
        className="flex items-center bg-vibe-bg-tertiary rounded p-0.5 [@media(pointer:coarse)]:hidden"
      >
        {/* Dashboard tab P1 (§11 LOCKED 2026-07-28) — leftmost primary tab +
            first-run default. The user-facing onboarding "Business OS" home.
            See docs/20260728/dashboard_onboarding_design/20260728_DESIGN_DASHBOARD_TAB_ONBOARDING_BUSINESS_OS.md */}
        <button
          type="button"
          aria-pressed={viewMode === 'dashboard'}
          title={tip('viewDashboard')}
          onClick={() => setViewMode('dashboard')}
          className={clsx(
            'flex items-center gap-1.5 px-3 py-1 text-sm rounded transition-colors',
            '[@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]',
            viewMode === 'dashboard'
              ? 'bg-vibe-accent text-white'
              : 'text-vibe-text-secondary hover:text-vibe-text'
          )}
        >
          <LayoutDashboard className="w-4 h-4" />
          Dashboard
        </button>
        <button
          type="button"
          aria-pressed={viewMode === 'designer'}
          title={tip('viewDesigner')}
          onClick={() => setViewMode('designer')}
          className={clsx(
            'flex items-center gap-1.5 px-3 py-1 text-sm rounded transition-colors',
            '[@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]',
            viewMode === 'designer'
              ? 'bg-vibe-accent text-white'
              : 'text-vibe-text-secondary hover:text-vibe-text'
          )}
        >
          <Code className="w-4 h-4" />
          Designer
        </button>
        {/* 1.168.0 Phase 1 — 3rd top tab "Knowledge". Placeholder shell only;
            tenant-corpus dashboard ships in Phase 2+. See
            docs/20260520/20260520_180000_DESIGN_KNOWLEDGE_TAB_TENANT_CORPUS_DASHBOARD.md */}
        <button
          type="button"
          aria-pressed={viewMode === 'knowledge'}
          title={tip('viewKnowledge')}
          onClick={() => setViewMode('knowledge')}
          className={clsx(
            'flex items-center gap-1.5 px-3 py-1 text-sm rounded transition-colors',
            '[@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]',
            viewMode === 'knowledge'
              ? 'bg-vibe-accent text-white'
              : 'text-vibe-text-secondary hover:text-vibe-text'
          )}
        >
          <BookOpen className="w-4 h-4" />
          Knowledge
        </button>
        <button
          type="button"
          aria-pressed={viewMode === 'terminals'}
          title={tip('viewTerminals')}
          onClick={() => setViewMode('terminals')}
          className={clsx(
            'flex items-center gap-1.5 px-3 py-1 text-sm rounded transition-colors',
            '[@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]',
            viewMode === 'terminals'
              ? 'bg-vibe-accent text-white'
              : 'text-vibe-text-secondary hover:text-vibe-text'
          )}
        >
          <TerminalIcon className="w-4 h-4" />
          Terminals
        </button>
        {/* Briefing Room M1 (2026-05-31) — 4th tab "Modules" for loadable
            vibe-designer modules. First module: The Briefing Room (007 RPG
            GM console). ModuleHostPane (placeholder in M1; lazy-loaded
            module SPA in M3+) renders inside TriShellLayout's modules
            shell. See apps/briefing-room-module/schema/host_probe.md. */}
        <button
          type="button"
          aria-pressed={viewMode === 'modules'}
          title={tip('viewModules')}
          onClick={() => setViewMode('modules')}
          className={clsx(
            'flex items-center gap-1.5 px-3 py-1 text-sm rounded transition-colors',
            '[@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]',
            viewMode === 'modules'
              ? 'bg-vibe-accent text-white'
              : 'text-vibe-text-secondary hover:text-vibe-text'
          )}
        >
          <Boxes className="w-4 h-4" />
          Modules
        </button>
        {/* 2026-06-26 — 5th tab "Deploys": manage + undeploy this tenant's
            public deployments (DeploysShell). */}
        <button
          type="button"
          aria-pressed={viewMode === 'deploys'}
          title={tip('viewDeploys')}
          onClick={() => setViewMode('deploys')}
          className={clsx(
            'flex items-center gap-1.5 px-3 py-1 text-sm rounded transition-colors',
            '[@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]',
            viewMode === 'deploys'
              ? 'bg-vibe-accent text-white'
              : 'text-vibe-text-secondary hover:text-vibe-text'
          )}
        >
          <Globe className="w-4 h-4" />
          Deploys
        </button>
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-2">
        {/* Build / Cancel button — E.21.34 swap-in-place: while a build
            is running, this becomes a Cancel button (one button, two
            modes). Reviewer §10 Q2 chose this over a separate cancel
            button for layout cohesion (TopBar right-side already has
            Build + Deploy + tenant + settings). */}
        {buildStatus.status === 'building' ? (
          <button
            data-testid="cancel-button"
            title={tip('cancelBuild')}
            onClick={handleCancel}
            disabled={!currentProject}
            className="flex items-center gap-2 px-3 py-1.5 text-sm rounded transition-colors bg-vibe-error/20 hover:bg-vibe-error/30 text-vibe-error [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        ) : (
          <div className="relative flex items-center">
            <button
              data-testid="build-button"
              title={tip('build')}
              onClick={() => void handleBuild()}
              disabled={!currentProject}
              className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-l transition-colors bg-vibe-bg-tertiary hover:bg-vibe-border text-vibe-text [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
            >
              <Play className="w-4 h-4" />
              Build
            </button>
            <button
              data-testid="build-mode-toggle"
              title="Choose build mode"
              onClick={() => setBuildMenuOpen((o) => !o)}
              disabled={!currentProject}
              className="flex items-center px-1.5 py-1.5 text-sm rounded-r border-l border-vibe-border/60 transition-colors bg-vibe-bg-tertiary hover:bg-vibe-border text-vibe-text [@media(pointer:coarse)]:min-h-[44px]"
            >
              <ChevronDown className={clsx('w-4 h-4 transition-transform', buildMenuOpen && 'rotate-180')} />
            </button>
            {buildStatus.buildScript === 'build:public' && (
              <span
                data-testid="build-kind-chip"
                title="Last build was a production build (build:public) — the deployable artifact"
                className="ml-1.5 px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded bg-vibe-success/20 text-vibe-success"
              >
                production
              </span>
            )}
            {buildMenuOpen && (
              <>
                <div className="fixed inset-0 z-[10006]" onClick={() => setBuildMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 z-[10007] w-64 rounded border border-vibe-border bg-vibe-bg-secondary shadow-lg py-1">
                  <button
                    data-testid="build-preview-option"
                    onClick={() => void handleBuildMode()}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-vibe-bg-tertiary text-vibe-text"
                  >
                    <span className="block font-medium">Preview build</span>
                    <span className="block text-xs text-vibe-text-secondary">
                      For iterating — feeds the in-Designer preview, never deployable.
                    </span>
                  </button>
                  <button
                    data-testid="build-production-option"
                    onClick={() => void handleBuildMode('production')}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-vibe-bg-tertiary text-vibe-text"
                  >
                    <span className="block font-medium">Production build</span>
                    <span className="block text-xs text-vibe-text-secondary">
                      Runs the project's build:public script — the artifact Deploy publishes.
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Deploy button — E.21.36 Workstream (b) swap-in-place pattern,
            mirroring E.21.34's Build/Cancel ternary. While deploy is
            in-flight, button text becomes "Deploying..." with spinner. */}
        <button
          data-testid="deploy-button"
          title={tip('deploy')}
          onClick={() => void handleDeploy()}
          disabled={!currentProject || isDeployingNow}
          className={clsx(
            'flex items-center gap-2 px-3 py-1.5 text-sm rounded transition-colors',
            '[@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]',
            // 1.126.0 Phase 2c: hidden on touch (moved to MobileMenuDrawer).
            '[@media(pointer:coarse)]:hidden',
            'bg-vibe-accent hover:bg-vibe-accent-hover text-white',
            (!currentProject || isDeployingNow) && 'opacity-60 cursor-not-allowed'
          )}
        >
          {isDeployingNow ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Deploying...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              Deploy
            </>
          )}
        </button>

        {/* Undeploy button — 2026-06-26. Only shown when the current project
            has a LIVE public deploy. Two-click confirm (destructive: takes a
            public URL down). Swap-in-place "Removing..." while DELETE runs. */}
        {(isLiveDeploy || isRemoving) && (
          isRemoving ? (
            <button
              data-testid="undeploy-button"
              disabled
              className="flex items-center gap-2 px-3 py-1.5 text-sm rounded bg-vibe-error/10 text-vibe-error opacity-70 [@media(pointer:coarse)]:hidden"
            >
              <Loader2 className="w-4 h-4 animate-spin" />
              Removing...
            </button>
          ) : confirmUndeploy ? (
            <span className="flex items-center gap-1 [@media(pointer:coarse)]:hidden">
              <button
                data-testid="undeploy-confirm-button"
                onClick={() => void handleUndeploy()}
                className="flex items-center gap-2 px-3 py-1.5 text-sm rounded bg-vibe-error/20 hover:bg-vibe-error/30 text-vibe-error"
              >
                <Trash2 className="w-4 h-4" />
                Confirm take-down
              </button>
              <button
                data-testid="undeploy-cancel-button"
                onClick={() => setConfirmUndeploy(false)}
                className="px-2 py-1.5 text-sm rounded text-vibe-text-secondary hover:text-vibe-text"
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              data-testid="undeploy-button"
              title={tip('undeploy')}
              onClick={() => setConfirmUndeploy(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm rounded transition-colors bg-vibe-bg-tertiary hover:bg-vibe-error/20 hover:text-vibe-error text-vibe-text-secondary [@media(pointer:coarse)]:hidden"
            >
              <Trash2 className="w-4 h-4" />
              Undeploy
            </button>
          )
        )}

        {/* Separator */}
        <div className="w-px h-6 bg-vibe-border mx-2" />

        {/* Tenant display — read-only, sourced from /api/me via tenantStore.
            1.126.0 Phase 2c: hidden on touch (moved to MobileMenuDrawer).
            1.212.0 (R3 LIVE 2026-05-27): fallback changed from 'no tenant'
            (alarming/wrong-looking when tenantStore is mid-fetch) to '…'
            (loading indicator). Backend's /api/me is the source of truth;
            an empty/loading state is correct, not an error. */}
        <div className="flex items-center gap-2 px-3 py-1.5 text-sm bg-vibe-bg-tertiary rounded [@media(pointer:coarse)]:hidden">
          <Cloud className="w-4 h-4 text-vibe-text-secondary" />
          <span className="text-vibe-text">
            {tenantId ?? '…'}
          </span>
        </div>

        <UserMenu />

        {/* 1.555.0 — MIND Chat 🧠 trigger (moved from a fixed bottom-right FAB
            that overlapped the Pi composer). Opens the panel in MindChatWidget. */}
        <button
          data-testid="mind-chat-button"
          onClick={() => openMindChat()}
          aria-label="Ask MIND"
          title="Ask MIND"
          aria-expanded={mindChatOpen}
          className="p-2 text-vibe-text-secondary hover:text-vibe-text hover:bg-vibe-bg-tertiary rounded transition-colors [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
        >
          <span className="text-base leading-none" aria-hidden="true">🧠</span>
        </button>

        {/* 2026-06-27 — Help: toggle help labels / take the guided tour. */}
        <HelpButton />

        {/* Settings — Phase B.1a opens a SettingsMenu dropdown with the
            auto-rebuild-on-save toggle. relative wrapper anchors the
            absolute-positioned dropdown. */}
        <div className="relative">
          <button
            data-testid="settings-button"
            onClick={() => setSettingsMenuOpen((v) => !v)}
            aria-label="Open settings menu"
            title={tip('settings')}
            aria-expanded={settingsMenuOpen}
            className="p-2 text-vibe-text-secondary hover:text-vibe-text hover:bg-vibe-bg-tertiary rounded transition-colors [@media(pointer:coarse)]:min-w-[44px] [@media(pointer:coarse)]:min-h-[44px]"
          >
            <Settings className="w-4 h-4" />
          </button>
          {/* Conditionally mount: SettingsMenu calls useToast() which
              throws outside ToastProvider. TopBar tests intentionally do
              not wrap in ToastProvider (they assert TopBar shape only).
              Lazy-mount keeps the test tree minimal AND avoids the hook
              cost when the menu is closed in production. */}
          {settingsMenuOpen && (
            <SettingsMenu
              open={true}
              onClose={() => setSettingsMenuOpen(false)}
            />
          )}
        </div>
      </div>
    </div>
    {/* 1.126.0 Phase 2c — MobileMenuDrawer sibling. Conditionally mounted
        so its `useAuth()` hook doesn't fire (and throw outside AuthProvider)
        when the drawer isn't open. Mirrors the SettingsMenu lazy-mount
        pattern above. The drawer's own `if (!open) return null` is then
        redundant but harmless. */}
    {mobileMenuOpen && <MobileMenuDrawer />}
    </>
  );
}
